import { useEffect, useState, type FormEvent } from "react";
import { WalletEditor } from "../components/WalletEditor";
import { SpotKeyBlock } from "../components/spot/SpotKeyBlock";
import { ViewerTokenCard } from "../components/ViewerTokenCard";
import { useAutopilot } from "../components/autopilotContext";
import { api } from "../api/client";
import { useViewer } from "../auth/authContext";
import { useResource } from "../api/useResource";
import { formatToken, shorten } from "../lib/format";
import type { WalletState } from "../types";

export function SettingsPage() {
  // The Bybit key block exists only while the spot engine does (`AutopilotView.spot` is null with
  // AUTO_POOLS_SPOT=off): its mount reads /spot/key, which such a server does not serve.
  const spotEngine = useAutopilot().data?.spot != null;
  const viewer = useViewer();
  const wallet = useResource<WalletState>("wallet", (signal) =>
    api("/wallet", { signal, sensitive: true }),
  );
  return (
    <>
      <header className="page-header">
        <div>
          <h1>Settings</h1>
          <p>Wallet and account security. Sensitive values are not stored in the browser.</p>
        </div>
      </header>
      <div className="settings-grid">
        <section className="panel settings-card">
          <h2>Signing wallet</h2>
          {wallet.loading && <p className="muted">Checking vault…</p>}
          {wallet.error && <p className="inline-alert error">{wallet.error.message}</p>}
          {wallet.data && (
            <div className="wallet-status">
              <span className={`wallet-orb ${wallet.data.locked ? "locked" : ""}`}>◎</span>
              <div>
                <b>
                  {wallet.data.configured
                    ? shorten(wallet.data.address ?? "address unavailable", 10)
                    : "Not configured"}
                </b>
                <p>
                  {wallet.data.locked
                    ? "Vault locked: signing unavailable"
                    : "Vault available to explicitly authorized background rules"}
                </p>
                {wallet.data.balanceSol != null && (
                  <small className="amount-line">
                    Balance: {formatToken(wallet.data.balanceSol)} SOL
                  </small>
                )}
              </div>
            </div>
          )}
          {wallet.data && (
            <WalletEditor
              key={wallet.data.generation ?? wallet.data.address ?? "new"}
              wallet={wallet.data}
              onImported={wallet.reload}
            />
          )}
        </section>
        {!viewer && (
          <section className="panel settings-card">
            <h2>Account access</h2>
            <PasswordChange />
          </section>
        )}
        {spotEngine && <SpotKeyBlock />}
        {!viewer && <ViewerTokenCard />}
      </div>
      <section className="panel security-explainer">
        <div>
          <span>01</span>
          <h3>Database is not enough</h3>
          <p>
            The seed is stored only in an encrypted envelope. A database copy alone cannot decrypt
            it.
          </p>
        </div>
        <div>
          <span>02</span>
          <h3>Reauthentication</h3>
          <p>
            A session opens the panel, but wallet replacement, seed reveal, and financial commands
            require the account password.
          </p>
        </div>
        <div>
          <span>03</span>
          <h3>Security boundary</h3>
          <p>
            Full access to the running server remains a hot-wallet risk. Keep an independent seed
            backup.
          </p>
        </div>
      </section>
    </>
  );
}

function PasswordChange() {
  const [password, setPassword] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [message, setMessage] = useState<{ tone: "error" | "good"; text: string } | null>(null);
  const [pending, setPending] = useState(false);
  useEffect(
    () => () => {
      setPassword("");
      setNext("");
      setConfirm("");
    },
    [],
  );
  async function submit(event: FormEvent) {
    event.preventDefault();
    if (next !== confirm) {
      setMessage({ tone: "error", text: "New passwords do not match" });
      return;
    }
    setPending(true);
    setMessage(null);
    try {
      await api("/auth/password", {
        method: "POST",
        body: { password, newPassword: next },
        sensitive: true,
      });
      setPassword("");
      setNext("");
      setConfirm("");
      window.dispatchEvent(new Event("auto-pools:unauthorized"));
    } catch (reason) {
      setMessage({
        tone: "error",
        text: reason instanceof Error ? reason.message : "Could not change password",
      });
    } finally {
      setPending(false);
    }
  }
  return (
    <form
      className="stack password-form"
      onSubmit={(event) => {
        void submit(event);
      }}
    >
      <h3>Change password</h3>
      <label>
        Current password
        <input
          type="password"
          autoComplete="current-password"
          value={password}
          onChange={(event) => {
            setPassword(event.target.value);
          }}
          required
        />
      </label>
      <label>
        New password
        <input
          type="password"
          autoComplete="new-password"
          value={next}
          onChange={(event) => {
            setNext(event.target.value);
          }}
          required
          minLength={12}
        />
      </label>
      <label>
        Repeat new password
        <input
          type="password"
          autoComplete="new-password"
          value={confirm}
          onChange={(event) => {
            setConfirm(event.target.value);
          }}
          required
        />
      </label>
      <button className="button ghost" disabled={pending}>
        {pending ? "Updating vault envelope…" : "Change password"}
      </button>
      {message && <p className={`inline-alert ${message.tone}`}>{message.text}</p>}
    </form>
  );
}
